import { LayoutHeader } from "@/Header";
import { createFileRoute, useRouter, useLocation } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { format, parse } from "date-fns";
import MessageModal from "@/MessageModal";
import { Dialog } from "@/Dialog";
import { CalendarNew as CalendarComponent } from "@/CalendarNew";
import {
  useTicketIssueDetail,
  useUpdateTicketIssueExpired,
  useUpdateTicketIssueStatus,
  type TicketIssueListItem,
} from "~/services/business/myPage";

/** API 리턴 기준 상세 타입 (data 필드와 동일) */
type DetailRow = {
  purchaseTicketSeq: number;
  userSeq?: number;
  userName: string;
  ticketName: string;
  qty: number;
  remainQty?: number;
  dtRegist: string;
  dtExpired: string;
  paymentType: string;
  status: string;
  cdProductType?: string;
  /** 최초 이용일 방식: 최초 이용일로부터 사용 가능 일수 (null이면 기존 방식) */
  usageDurationDays?: number | null;
  /** 최초 이용(방문)일 — usageDurationDays가 있고 이 값이 null이면 아직 이용 시작 전 */
  dtFirstUse?: string | null;
  /** 구매 티켓의 현재 실제 만료일 */
  actualDtExpired?: string | null;
};

function apiRowToDetailRow(row: TicketIssueListItem): DetailRow {
  return {
    purchaseTicketSeq: row.purchaseTicketSeq,
    userSeq: row.userSeq,
    userName: row.userName ?? "",
    ticketName: row.ticketName ?? "",
    qty: row.qty ?? 0,
    remainQty: row.remainQty,
    dtRegist: row.dtRegist ?? "",
    dtExpired: row.dtExpired ?? "",
    paymentType: row.paymentType ?? "",
    status: row.status ?? "",
    cdProductType: row.cdProductType,
    usageDurationDays: row.usageDurationDays ?? null,
    dtFirstUse: row.dtFirstUse ?? null,
    actualDtExpired: row.actualDtExpired ?? null,
  };
}

export const Route = createFileRoute(
  "/business/layout/user-subscription-issue-detail"
)({
  component: SubscriptionIssueDetail,
  validateSearch: (search: Record<string, unknown>) => ({
    purchaseTicketSeq: Number(search.purchaseTicketSeq) || 0,
  }),
});

function SubscriptionIssueDetail() {
  const { history } = useRouter();
  const location = useLocation();
  const { purchaseTicketSeq } = Route.useSearch();

  const [isRecallConfirmOpen, setIsRecallConfirmOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isValidDateModalOpen, setIsValidDateModalOpen] = useState(false);

  const { data: detailData, isLoading: isDetailLoading } = useTicketIssueDetail(
    { purchaseTicketSeq }
  );
  const updateTicketIssueExpiredMutation = useUpdateTicketIssueExpired();
  const updateTicketIssueStatusMutation = useUpdateTicketIssueStatus();

  const detail = useMemo((): DetailRow | undefined => {
    if (detailData?.data) {
      return apiRowToDetailRow(detailData.data);
    }
    const stateRow = (location.state as { row?: TicketIssueListItem })?.row;
    if (stateRow && stateRow.purchaseTicketSeq === purchaseTicketSeq) {
      return apiRowToDetailRow(stateRow);
    }
    return undefined;
  }, [purchaseTicketSeq, location.state, detailData?.data]);

  const [validEndDt, setValidEndDt] = useState<string>("");
  const [statusOverride, setStatusOverride] = useState<string | null>(null);

  // 최초 이용일 방식 & 아직 첫 이용 전 (dtExpired에는 임시 상한인 발급일+1년이 들어있음)
  const isFirstUsePending =
    detail?.usageDurationDays != null && !detail?.dtFirstUse;

  const initialValidEndDt = useMemo(() => {
    // 최초 이용일 방식으로 이용이 시작된 경우 로그의 만료일 대신 실제 만료일 기준
    const base =
      detail?.usageDurationDays != null && detail?.dtFirstUse
        ? detail?.actualDtExpired || detail?.dtExpired
        : detail?.dtExpired;
    if (!base) return "";
    const d = new Date(base);
    return d.toISOString().slice(0, 10);
  }, [detail]);

  const appliedValidEndDt = validEndDt || initialValidEndDt;

  const formatDateTime = (dt: string) => {
    if (!dt) return "-";
    const d = new Date(dt);
    return d.toLocaleString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const handleSaveValidity = async () => {
    if (!appliedValidEndDt || !detail) return;
    try {
      await updateTicketIssueExpiredMutation.mutateAsync({
        purchaseTicketSeq: detail.purchaseTicketSeq,
        date: appliedValidEndDt,
      });
      (detail as { dtExpired: string }).dtExpired = appliedValidEndDt;
      setSuccessMessage("유효기간이 수정되었습니다.");
      setIsSuccessOpen(true);
    } catch {
      // 에러는 mutation에서 throw, 전역 또는 토스트 처리 가능
    }
  };

  const handleRecallConfirm = async () => {
    setIsRecallConfirmOpen(false);
    if (!detail) return;
    try {
      const res = await updateTicketIssueStatusMutation.mutateAsync({
        purchaseTicketSeq: detail.purchaseTicketSeq,
        isRevoke: true,
      });
      // HTTP는 항상 200이고 본문 statusCode로 성공/실패가 내려온다.
      // (이미 회수된 건 등은 실패 코드 → 성공 문구를 띄우면 안 됨)
      if (res?.statusCode !== 200 && res?.statusCode !== 201) {
        setErrorMessage(
          res?.message || "회수에 실패했습니다. 잠시 후 다시 시도해 주세요."
        );
        return;
      }
      setStatusOverride("REVOKE");
      (detail as { status: string }).status = "REVOKE";
      setSuccessMessage("발급이 회수되었습니다. 기록은 유지됩니다.");
      setIsSuccessOpen(true);
    } catch {
      setErrorMessage("회수에 실패했습니다. 잠시 후 다시 시도해 주세요.");
    }
  };

  /** 발급상태 코드 → 표시 텍스트 (ISSUE, REVOKE, USE, EXPIRED) */
  const getStatusLabel = (code: string) => {
    switch (code) {
      case "ISSUE":
        return "발급";
      case "REVOKE":
        return "회수";
      case "USE":
        return "사용";
      case "EXPIRED":
        return "만료";
      case "CANCEL":
        return "취소";
      default:
        return code || "-";
    }
  };

  const displayStatus = statusOverride ?? detail?.status ?? "";

  if (isDetailLoading && !detail) {
    return (
      <>
        <LayoutHeader
          title="이용권 발급 상세"
          hasBackBtn
          hasFootPrint={false}
        />
        <div className="flex justify-center items-center py-20 text-[#666666]">
          로딩 중...
        </div>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <LayoutHeader
          title="이용권 발급 상세"
          hasBackBtn
          hasFootPrint={false}
        />
        <div className="flex justify-center items-center py-20 text-[#666666]">
          데이터를 불러올 수 없습니다.
        </div>
      </>
    );
  }

  return (
    <>
      <LayoutHeader title="이용권 발급 상세" hasBackBtn hasFootPrint={false} />
      <div className="flex-1 min-h-0 overflow-y-auto bg-[#F5F5F5] px-4 pt-4">
        {/* 상세 정보 */}
        <div className="bg-white rounded-xl border border-[#E9E9E9] overflow-hidden">
          <div className="divide-y divide-[#E9E9E9]">
            <div className="flex justify-between items-center px-4 py-3">
              <span className="text-[13px] text-[#666666] w-28 shrink-0">
                이름
              </span>
              <span className="text-[14px] text-[#333] flex-1 text-right">
                {detail.userName ?? "-"}
              </span>
            </div>
            <div className="flex justify-between items-center px-4 py-3">
              <span className="text-[13px] text-[#666666] w-28 shrink-0">
                이용권
              </span>
              <span className="text-[14px] text-[#333] flex-1 text-right">
                {detail.ticketName ?? "-"}
              </span>
            </div>
            <div className="flex justify-between items-center px-4 py-3">
              <span className="text-[13px] text-[#666666] w-28 shrink-0">
                {detail?.cdProductType === "PRODUCT_TYPE_HOTEL"
                  ? "이용 박수"
                  : "횟수"}
              </span>
              <span className="text-[14px] flex-1 text-right">
                {detail?.cdProductType === "PRODUCT_TYPE_HOTEL" ? (
                  // 호텔은 박수 누적 차감 없이 1회 사용 시 전량 소진 — 박수만 표시
                  // (사용 전/완료 상태는 상단 발급 상태에서 이미 노출됨)
                  detail.qty != null && detail.qty > 0 ? (
                    <span className="text-[#333]">{detail.qty}박</span>
                  ) : (
                    <span className="text-[#333]">-</span>
                  )
                ) : typeof detail?.remainQty === "number" &&
                  detail.qty != null &&
                  detail.qty > 0 ? (
                  <>
                    <span className="text-[#333]">
                      {detail.qty - detail.remainQty}회 사용
                    </span>
                    <span
                      className={
                        detail.remainQty === 0
                          ? "ml-1 text-[#999]"
                          : "ml-1 font-medium text-orange-500"
                      }
                    >
                      ({detail.remainQty}회 남음)
                    </span>
                  </>
                ) : detail?.qty != null && detail.qty > 0 ? (
                  <span className="text-[#333]">{detail.qty}회</span>
                ) : (
                  <span className="text-[#333]">-</span>
                )}
              </span>
            </div>
            <div className="flex justify-between items-center px-4 py-3">
              <span className="text-[13px] text-[#666666] w-28 shrink-0">
                발급일시
              </span>
              <span className="text-[14px] text-[#333] flex-1 text-right">
                {formatDateTime(detail.dtRegist)}
              </span>
            </div>
            <div className="flex justify-between items-center px-4 py-3">
              <span className="text-[13px] text-[#666666] w-28 shrink-0">
                유효기간
              </span>
              <span className="text-[14px] text-[#333] flex-1 text-right">
                {isFirstUsePending
                  ? `첫 이용일로부터 ${detail.usageDurationDays}일`
                  : initialValidEndDt || "-"}
              </span>
            </div>
            {isFirstUsePending && (
              <div className="px-4 py-1.5">
                <p className="text-[12px] text-orange-500 text-right">
                  아직 이용 전이라 만료일이 확정되지 않았습니다.
                  <br />
                  (예약 가능 상한: {initialValidEndDt})
                </p>
              </div>
            )}
            {detail.usageDurationDays != null && detail.dtFirstUse && (
              <div className="flex justify-between items-center px-4 py-3">
                <span className="text-[13px] text-[#666666] w-28 shrink-0">
                  이용 시작일
                </span>
                <span className="text-[14px] text-[#333] flex-1 text-right">
                  {detail.dtFirstUse}
                </span>
              </div>
            )}
            <div className="flex justify-between items-center px-4 py-3">
              <span className="text-[13px] text-[#666666] w-28 shrink-0">
                유형
              </span>
              <span className="text-[14px] text-[#333] flex-1 text-right">
                {detail.paymentType || "-"}
              </span>
            </div>
            <div className="flex justify-between items-center px-4 py-3">
              <span className="text-[13px] text-[#666666] w-28 shrink-0">
                발급상태
              </span>
              <span
                className={`text-[14px] flex-1 text-right font-medium ${
                  displayStatus === "ISSUE"
                    ? "text-green-600"
                    : "text-gray-500"
                }`}
              >
                {getStatusLabel(displayStatus)}
              </span>
            </div>
          </div>
        </div>

        {/* 유효기간 수정: 발급(ISSUE) 또는 사용(USE) 상태일 때만 날짜 선택·저장 버튼 표시 */}
        <div className="mt-4 bg-white rounded-xl border border-[#E9E9E9] p-4">
          <div className="text-[14px] font-semibold text-[#333] mb-3">
            유효기간 수정
          </div>
          {displayStatus !== "ISSUE" && displayStatus !== "USE" && (
            <p className="text-[13px] text-[#666666] mb-3">
              발급 또는 사용 상태일 때만 유효기간을 수정할 수 있습니다.
            </p>
          )}
          {isFirstUsePending &&
            (displayStatus === "ISSUE" || displayStatus === "USE") && (
              <p className="text-[13px] text-[#666666] mb-3">
                첫 이용 시 만료일이 자동 확정되는 이용권입니다. 이용 시작 후에
                유효기간을 수정할 수 있습니다.
              </p>
            )}
          {!isFirstUsePending &&
            (displayStatus === "ISSUE" || displayStatus === "USE") && (
            <div className="flex gap-2 items-center">
              <button
                type="button"
                onClick={() => setIsValidDateModalOpen(true)}
                className="flex-1 px-3 py-2 text-[14px] text-left border border-[#E9E9E9] rounded-lg bg-white"
              >
                {appliedValidEndDt
                  ? format(
                      parse(appliedValidEndDt, "yyyy-MM-dd", new Date()),
                      "yyyy.MM.dd"
                    )
                  : "날짜 선택"}
              </button>
              <button
                type="button"
                onClick={() => void handleSaveValidity()}
                disabled={
                  appliedValidEndDt === initialValidEndDt ||
                  updateTicketIssueExpiredMutation.isPending
                }
                className="px-4 py-2 text-[14px] font-semibold text-white bg-orange-400 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateTicketIssueExpiredMutation.isPending ? "저장 중..." : "저장"}
              </button>
            </div>
          )}
        </div>

        {/* 유효기간 선택 달력 모달 */}
        <MessageModal
          isOpen={isValidDateModalOpen}
          onRequestClose={() => setIsValidDateModalOpen(false)}
          className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none animate-scaleIn"
          overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
          shouldCloseOnOverlayClick={false}
        >
          <CalendarComponent
            key={appliedValidEndDt || "today"}
            disabled={false}
            disablePast={true}
            defaultToday={
              appliedValidEndDt
                ? parse(appliedValidEndDt, "yyyy-MM-dd", new Date())
                : new Date()
            }
            dayOnClick={(day) => {
              if (day) {
                const dateString = format(day, "yyyy-MM-dd");
                setValidEndDt(dateString);
                setIsValidDateModalOpen(false);
              }
            }}
            selectedDay={
              appliedValidEndDt
                ? parse(appliedValidEndDt, "yyyy-MM-dd", new Date())
                : null
            }
            schedules={{}}
          />
          <div className="px-4 pt-2">
            <button
              type="button"
              onClick={() => setIsValidDateModalOpen(false)}
              className="w-full py-3 text-[14px] font-semibold text-[#666666] border border-[#E9E9E9] rounded-lg bg-white"
            >
              취소
            </button>
          </div>
        </MessageModal>

        {/* 발급 회수: 유형 offline + 발급상태 발급/사용 + remainQty 조건일 때만 표시 */}
        {detail?.paymentType?.toLowerCase() === "offline" &&
          (displayStatus === "ISSUE" || displayStatus === "USE") &&
          typeof detail?.remainQty === "number" &&
          detail.remainQty > 0 &&
          detail.remainQty <= (detail?.qty ?? 0) && (
            <div className="mt-4 bg-white rounded-xl border border-[#E9E9E9] p-4">
              <div className="text-[14px] font-semibold text-[#333] mb-2">
                발급 회수
              </div>
              <p className="text-[13px] text-[#666666] mb-3">
                발급 회수시 고객이 이용하지 않은 이용권에 한하여 회수됩니다.  (등원완료 이전 회수 가능)
              </p>
              <button
                type="button"
                onClick={() => setIsRecallConfirmOpen(true)}
                className="w-full py-3 text-[14px] font-semibold text-white bg-orange-400 rounded-lg"
              >
                발급 회수
              </button>
            </div>
          )}

        {/* 예약하기: 발급상태 발급/사용 + remainQty <= qty, remainQty !== 0, 유형 오프라인일 때만 표시 */}
        {(displayStatus === "ISSUE" || displayStatus === "USE") &&
          typeof detail?.remainQty === "number" &&
          detail.remainQty > 0 &&
          detail.remainQty <= (detail?.qty ?? 0) &&
          detail?.paymentType?.toLowerCase() === "offline" && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() =>
                  history.push("/business/layout/reservation-with-issued-ticket", {
                    userSeq: detail.userSeq,
                    purchaseTicketSeq: detail.purchaseTicketSeq,
                    cdProductType: detail.cdProductType,
                    ticketName: detail.ticketName,
                    remainQty: detail.remainQty,
                    qty: detail.qty,
                    userName: detail.userName,
                  })
                }
                className="w-full py-3 text-[14px] font-semibold text-white bg-orange-400 rounded-xl"
              >
                예약하기
              </button>
            </div>
          )}
      </div>

      {/* 발급 회수 확인 모달 */}
      <Dialog
        visible={isRecallConfirmOpen}
        onClose={() => setIsRecallConfirmOpen(false)}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="flex flex-col mb-2">
            <div className="text-sm text-center text-[#666666]">
              발급을 취소하시겠습니까?
            </div>
          </div>
          <div className="flex gap-3 mt-2">
            <button
              className="flex-1 py-2 font-semibold text-gray-600 border border-[#E5E5E5] rounded-full"
              onClick={() => setIsRecallConfirmOpen(false)}
            >
              취소
            </button>
            <button
              className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => void handleRecallConfirm()}
              disabled={updateTicketIssueStatusMutation.isPending}
            >
              {updateTicketIssueStatusMutation.isPending ? "처리 중..." : "확인"}
            </button>
          </div>
        </div>
      </Dialog>

      {/* 실패 안내 모달 — 회수가 실제로 되지 않았는데 성공 문구가 뜨지 않도록 */}
      <Dialog
        visible={!!errorMessage}
        onClose={() => setErrorMessage("")}
        persist={false}
      >
        <div className="flex flex-col gap-1">
          <div className="text-sm text-center text-[#666666] mb-2">
            {errorMessage}
          </div>
          <button
            className="py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={() => setErrorMessage("")}
          >
            확인
          </button>
        </div>
      </Dialog>

      {/* 성공 모달 */}
      <MessageModal
        isOpen={isSuccessOpen}
        onRequestClose={() => {
          setIsSuccessOpen(false);
          history.push("/business/layout/user-subscription-issue-history");
        }}
        className="pt-6 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
        title="완료"
      >
        <div className="flex flex-col gap-4 items-center">
          <div className="text-center text-gray-700">{successMessage}</div>
          <button
            onClick={() => {
              setIsSuccessOpen(false);
              history.push("/business/layout/user-subscription-issue-history");
            }}
            className="py-3 w-full text-base font-semibold text-white bg-orange-400 rounded-lg"
          >
            확인
          </button>
        </div>
      </MessageModal>
    </>
  );
}
